import { useEffect, useState } from 'react'
import type { Location, LocationInput } from '../api/locations'

const EMPTY = { name: '', country: '', latitude: '', longitude: '' }

// Create and edit share one form: `editing` is null when adding, and the location
// being changed when editing. Keeping them together means the validation and layout
// exist once rather than in two nearly-identical components.
//
// The numeric fields are held as strings, deliberately. Binding a number input
// straight to a number makes a half-typed "-" or "51." unrepresentable, so the field
// fights the user mid-entry; parsing once on submit does not.
export function LocationForm({
  editing,
  busy,
  onSubmit,
  onCancel,
}: {
  editing: Location | null
  busy: boolean
  onSubmit: (input: LocationInput) => void
  onCancel: () => void
}) {
  const [fields, setFields] = useState(EMPTY)
  const [error, setError] = useState<string | null>(null)

  // Reload the form whenever the selection changes — including to null, which resets
  // it for the next add.
  useEffect(() => {
    setFields(
      editing
        ? {
            name: editing.name,
            country: editing.country,
            latitude: String(editing.latitude),
            longitude: String(editing.longitude),
          }
        : EMPTY,
    )
    setError(null)
  }, [editing])

  function set(field: keyof typeof EMPTY, value: string) {
    setFields((current) => ({ ...current, [field]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const name = fields.name.trim()
    const country = fields.country.trim()
    const latitude = Number(fields.latitude)
    const longitude = Number(fields.longitude)

    // The API validates all of this too and is the real authority — this pass just
    // saves a round trip and points at the offending field.
    if (!name || !country) {
      setError('Name and country are both required.')
      return
    }
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      setError('Latitude must be a number between -90 and 90.')
      return
    }
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      setError('Longitude must be a number between -180 and 180.')
      return
    }

    setError(null)
    onSubmit({ name, country, latitude, longitude })
  }

  return (
    <form className="location-form" onSubmit={handleSubmit}>
      <h2 className="location-form__title">
        {editing ? `Edit ${editing.name}` : 'Add a location'}
      </h2>

      <div className="location-form__grid">
        <label className="login__field">
          <span>Name</span>
          <input
            value={fields.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={100}
            placeholder="Manchester"
          />
        </label>

        <label className="login__field">
          <span>Country</span>
          <input
            value={fields.country}
            onChange={(e) => set('country', e.target.value)}
            maxLength={100}
            placeholder="United Kingdom"
          />
        </label>

        <label className="login__field">
          <span>Latitude</span>
          <input
            value={fields.latitude}
            onChange={(e) => set('latitude', e.target.value)}
            inputMode="decimal"
            placeholder="53.4808"
          />
        </label>

        <label className="login__field">
          <span>Longitude</span>
          <input
            value={fields.longitude}
            onChange={(e) => set('longitude', e.target.value)}
            inputMode="decimal"
            placeholder="-2.2426"
          />
        </label>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="location-form__actions">
        <button type="submit" className="refresh" disabled={busy}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Add location'}
        </button>

        {editing && (
          <button type="button" className="logout" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
